<script lang="ts">
    import type {AxiosInstance} from 'axios';
    import type {NewsLetterSuccessResponseSmsType} from 'types/newsletter';

    import Button from 'general/components/button/Button.svelte';
    import Input from '../helpers/input/Input.svelte';

    import {duration, modal, dyData} from 'general/store';
    import {ENDPOINTS, ANALYTICS, COOKIE_VALUE, STATE, TEMPLATE, FLAGS} from 'general/constants';
    import {pushPopupEvent} from 'general/libs/pushDataLayer';
    import {setCookie} from 'general/libs/setCookie';
    import {getExtraUrlData, getTimezone} from 'general/libs/dy/helpers';
    import {getEmailFromLocalStorage} from 'general/libs/getEmailFromLocalStorage';
    import {logWithSentry} from 'sentry/sentry';

    import s from '../style.module.css';

    export const onClick: () => void = () => {};
    export let instance: AxiosInstance;
    export let template: (typeof TEMPLATE)[keyof typeof TEMPLATE] = TEMPLATE.CIRCLE;
    export let label = 'Enter phone number';

    const placeholderMask = '1-000-000-0000';
    const isIconVisible =
        (template === TEMPLATE.SQUARE_DESKTOP_COMMON &&
            ($modal.state === STATE.SMS || $modal.state === STATE.TYP)) ||
        (template === TEMPLATE.SQUARE_EXIT_REDESIGN && $modal.state === STATE.SMS);

    const isWithoutTYP =
        (template === TEMPLATE.SQUARE_EXIT || template === TEMPLATE.SQUARE_DESKTOP_COMMON) &&
        !$dyData.side.typ;

    $: placeholderText = placeholderMask;
    const handleInput = ({target}: Event) => {
        let value = (target as HTMLInputElement).value;

        if ($modal.phone.length === 0) {
            value = '1-' + value;
        } else {
            const phoneParts = /(1{1})(\d{0,3})(\d{0,3})(\d{0,4})/.exec(value.replace(/\D/g, ''));
            if (phoneParts) {
                value =
                    phoneParts[1] +
                    '-' +
                    phoneParts[2] +
                    (phoneParts[3] ? '-' + phoneParts[3] : '') +
                    (phoneParts[4] ? '-' + phoneParts[4] : '');
            }
        }

        modal.changePhone(value);
        placeholderText = value + placeholderMask.substring($modal.phone.length);
        (target as HTMLInputElement).setCustomValidity('');
    };

    const handleChange = ({target}: Event) => {
        (target as HTMLInputElement).setCustomValidity('');
    };

    const handleInvalid = ({target}: Event) => {
        (target as HTMLInputElement).setCustomValidity('Please match the format: x-xxx-xxx-xxxx');
        pushPopupEvent(`Error: Please match the format: x-xxx-xxx-xxxx`);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleSubmit = () => {
        try {
            const userEmail = $modal.email || getEmailFromLocalStorage();
            const cid = new URLSearchParams(window.location.search).get('cid') || null;
            const timezone = getTimezone();

            const preparedPhoneNumber = (phone = '') => {
                const phoneWithoutDivider = phone.replace(/\D/g, '');
                return phoneWithoutDivider.charAt(0) === '1'
                    ? phoneWithoutDivider
                    : `1${phoneWithoutDivider}`;
            };

            const {referral, ...extraUrlCookie} = getExtraUrlData();

            instance
                .post<NewsLetterSuccessResponseSmsType>(ENDPOINTS.CUSTOMER_SET_PHONE_NUMBER, {
                    email: userEmail,
                    phone: preparedPhoneNumber($modal.phone),
                    utmData: {...extraUrlCookie},
                    referral,
                    timezone,
                    cordialId: cid,
                    ...($dyData.smsKeyword ? {keyword: $dyData.smsKeyword} : {}),
                })
                .then(() => {
                    pushPopupEvent(ANALYTICS.POPUP_SUBSCRIBED_SMS, {number: $modal.phone});
                    setCookie(COOKIE_VALUE.SUBSCRIBER_PHONE, 'true', 365);

                    //TODO rename or delete after experimenting with recommendations in DY popups
                    setCookie('IS_FIRST_DAY_SUBSCRIBER', 'true', 1);
                    if (isWithoutTYP) {
                        modal.close();
                    } else {
                        duration.update('flip', {in: 600});
                        duration.update('opacity', {in: 350});
                        modal.changeState(STATE.TYP);
                    }
                })
                .catch(e => {
                    if (e?.message && e?.message !== 'Network Error') {
                        //TODO fix behavior after the backend be ready
                        setCookie(COOKIE_VALUE.SUBSCRIBER_PHONE, 'true', 365);

                        //TODO rename or delete after experimenting with recommendations in DY popups
                        setCookie('IS_FIRST_DAY_SUBSCRIBER', 'true', 1);

                        if (isWithoutTYP) {
                            modal.close();
                        } else {
                            duration.update('flip', {in: 600});
                            duration.update('opacity', {in: 350});
                            modal.changeState(STATE.TYP);
                        }
                    } else modal.close();
                });
        } catch (error) {
            logWithSentry(error as Error);
            modal.close();
        }
    };

    const expHandleSubmit = () => {
        setCookie(COOKIE_VALUE.SUBSCRIBER_PHONE, 'true', 365);
        setCookie('IS_FIRST_DAY_SUBSCRIBER', 'true', 1);
        modal.close();
    };
</script>

<form
    class="{s.form} {s[template] || ''} {s[$modal.state] || ''}"
    class:smallScreen={$dyData.isSmallerScreen}
    on:submit|preventDefault={$dyData.flag === FLAGS.SMS_OLD ? handleSubmit : expHandleSubmit}
>
    <Input
        bind:inputValue={$modal.phone}
        type="tel"
        pattern="^\+?\d&#123;1&#125;-?\d&#123;3&#125;-?\d&#123;3&#125;-?\d&#123;4&#125;$"
        {label}
        required
        isVisibleLabel
        {handleInput}
        {handleChange}
        {handleInvalid}
        marginBottom="10px"
        reminderStatus="error"
        maxlength={14}
        placeholder={template === TEMPLATE.EXP_SQUARE ? placeholderText : placeholderMask}
        {template}
        withIcon={isIconVisible}
    />
    <slot />

    <!-- We can't use common button cuz Button changes it's props before animation ends -->
    {#if template === TEMPLATE.SQUARE_DESKTOP_COMMON}
        <button class="{s.button} {s[template] || ''} {s[$modal.state]}">
            {$dyData.ctaButtonText?.sms || 'Send Me a Coupon'}
        </button>
    {:else}
        <Button
            {template}
            children={$dyData.ctaButtonText?.sms || 'Send Me a Coupon'}
            dyButtonData={$dyData.mainButton?.sms}
        />
    {/if}
</form>
